<ng-container *adaptLoading="!categories">
    <adapt-toolbar-title>
        @if (personId) {
            <h2>Edit vision</h2>
        } @else {
            <h2>Edit integrated vision</h2>
        }
    </adapt-toolbar-title>
    <div class="me-3 alert alert-light d-flex gap-2 align-items-center">
        @if (personId) {
            <span class="d-flex align-items-center gap-1">Edit owner vision for
                <adapt-link-person [personId]="personId"
                                   imageSize="sm"
                                   [showImage]="true"></adapt-link-person>.</span>
        } @else {
            <span>Select a category on the left to update the integrated vision statement for each defined category.</span>
            @if (editableVision?.extensions?.isIntegrated && editableVision?.personId) {
                <span class="d-flex align-items-center gap-1"><adapt-link-person [personId]="editableVision!.personId"
                                       imageSize="sm"
                                       [showImage]="true"></adapt-link-person> has been nominated to drive the integrated vision.</span>
            }
        }
    </div>
    <adapt-two-column-layout [fullPage]="true"
                             class="fill-page-height"
                             leftColumnWidthClasses="col-12 col-lg-5 col-xl-4 p-0">
        <div left-column
             class="d-flex flex-column gap-2 left-column">
            <h2>Categories</h2>
            <dx-list [dataSource]="categories"
                     selectionMode="single"
                     [focusStateEnabled]="false"
                     (onSelectionChanged)="onCategorySelected($event)"
                     (onContentReady)="onCategoryListReady($event)">
                <div *dxTemplate="let category of 'item'"
                     class="d-flex">
                    {{ category.name }}
                    @if (getEditableVisionForCategory(category.visionCategoryId)?.extensions?.isCompleted) {
                        <i class="ms-auto fal fa-fw fa-check text-success"
                           adaptTooltip="Completed"></i>
                    }
                </div>
            </dx-list>
        </div>
        <div right-column
             *adaptLoading="!editableVision"
             id="edit-vision-right-column"
             class="d-flex flex-column gap-3">
            <h2 class="my-0 d-flex align-items-center gap-2">{{ selectedCategory?.name }}
                <i class="fal fa-fw smaller-vision-button-text"
                   [ngClass]="{'fa-chevron-down': hideGuidance, 'fa-chevron-up': !hideGuidance}"
                   role="button"
                   (click)="hideGuidance = !hideGuidance"></i>
            </h2>
            <div [collapse]="hideGuidance"
                 class="alert alert-info p-3">
                @if (selectedCategory?.description) {
                    <div [froalaView]="selectedCategory?.description"></div>
                } @else if (selectedCategory?.descriptionSlug) {
                    <adapt-styled-implementation-kit [articleId]="getImplementationKitArticle(selectedCategory?.descriptionSlug!)">
                    </adapt-styled-implementation-kit>
                } @else {
                    <i>No guidance provided for this category.</i>
                }
            </div>

            @if (!personId) {
                @if (ownersVisions?.length) {
                    <h3 class="my-0">Owners visions</h3>
                    <!-- integrated vision - shows all owners visions -->
                    <div class="d-grid gap-3 owners-visions">
                        @for (ownersVision of ownersVisions; track ownersVision.visionId; let i = $index) {
                            <div class="d-flex flex-column gap-1 owner-vision"
                                 [ngClass]="'vision-person-color-border-' + (i % 12)">
                                <div class="d-flex align-items-center gap-2">
                                    <adapt-link-person [personId]="ownersVision.personId"
                                                       [showImage]="true"
                                                       imageSize="sm"
                                                       [linkDisabled]="true"></adapt-link-person>
                                    <button adaptButton="editBorderless"
                                            class="smaller-vision-button-text"
                                            (click)="editPersonalVision(ownersVision.personId!)"></button>
                                </div>
                                <div class="owner-vision-statement">
                                    @if (ownersVision.statement) {
                                        <div [froalaView]="ownersVision.statement"></div>
                                    } @else {
                                        <span class="text-muted small">
                                            <i>No vision statement defined.</i>
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }
            }

            <div class="d-flex flex-column gap-1">
                <div class="d-flex align-items-center gap-2 statement-label">
                    @if (personId) {
                        <h3 class="my-0">Vision statement</h3>
                    } @else {
                        <h3 class="my-0">Integrated vision</h3>
                    }

                    <ng-container *adaptLoading="aiBusy">
                        <adapt-menu [items]="aiHelperMenuItems"></adapt-menu>
                    </ng-container>
                </div>

                @if (editableVision) {
                    <adapt-html-editor [(contents)]="editableVision.statement"
                                       (contentsChange)="onVisionStatementChanged(editableVision)"
                                       data-test="vision-statement-editor"
                                       placeholderText="Enter vision statement here..."></adapt-html-editor>
                } @else {
                    <div class="alert alert-warning">Internal error: No vision statement entity</div>
                }
            </div>
            <div class="d-flex gap-1 align-items-center">
                @if (hasChanges) {
                    <button class="btn-sm"
                            adaptButton="save"
                            [adaptBlockingClick]="saveVision"
                            [adaptBlockingClickParam]="editableVision"></button>
                    <button class="btn-sm"
                            adaptButton="cancel"
                            [adaptBlockingClick]="discardVision"
                            [adaptBlockingClickParam]="editableVision"></button>
                } @else if (editableVision?.statement && !editableVision?.extensions?.isCompleted) {
                    <button class="btn-sm btn btn-primary me-2"
                            adaptButtonIcon="ok"
                            [adaptBlockingClick]="saveVision"
                            [adaptBlockingClickParam]="editableVision"
                            [unsubscribeOnDestroy]="false">Keep current vision</button>
                    <ng-container *ngTemplateOutlet="showLastUpdatedDate; context: { vision: editableVision }"></ng-container>
                } @else if (editableVision?.extensions?.isCompleted) {
                    @if (hasIncompleteCategory) {
                        <button class="btn-sm me-2"
                                adaptButton="workflowNext"
                                (click)="moveToNextCategory()">Next incomplete category</button>
                    }
                    <ng-container *ngTemplateOutlet="showLastUpdatedDate; context: { vision: editableVision }"></ng-container>
                    <span class="text-success ms-auto d-flex align-items-center gap-1">
                        <i class="fal fa-check"></i> Completed
                    </span>
                } @else {
                    <span class="text-warning ms-auto d-flex align-items-center gap-1">
                        <i class="fal fa-pen"></i> Incomplete
                    </span>
                }
            </div>
            @if (reviewResult && editableVision) {
                <div class="alert alert-secondary d-flex flex-column gap-2">
                    <div [froalaView]="reviewResult"></div>
                    <div class="d-flex gap-4">
                        <button class="btn btn-sm btn-link"
                                adaptButtonIcon="fal fa-paste"
                                (click)="replaceVisionStatement(editableVision)">Replace vision statement</button>
                        <button class="btn btn-sm btn-link"
                                adaptButtonIcon="fal fa-times"
                                (click)="reviewResult = undefined">Dismiss</button>
                    </div>
                </div>
            }
        </div>
    </adapt-two-column-layout>
    <ng-template #showLastUpdatedDate
                 let-vision="vision">
        @if (vision.lastUpdated) {
            <span class="text-muted small">Last updated:
                {{ vision.lastUpdated | adaptDateTime }}
            </span>
        }
    </ng-template>
</ng-container>
